import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list with a "Load More" button.
 * Extends PaginatedList but disables automatic infinite scrolling.
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    // Disable the automatic infinite scroll observer
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Update button visibility on initial load
    this.#updateLoadMoreButton();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Handles the load more button click.
   * Called via declarative event binding: on:click="results-list/handleLoadMore"
   */
  async handleLoadMore() {
    const button = this.refs.loadMoreButton;
    if (button && !Array.isArray(button)) {
      button.setAttribute('aria-busy', 'true');
    }

    const loaded = await this.loadNextPage();

    if (button && !Array.isArray(button)) {
      button.setAttribute('aria-busy', 'false');
    }

    // Update button visibility after loading
    this.#updateLoadMoreButton();
  }

  /**
   * Updates the visibility of the load more button based on whether there are more pages.
   */
  #updateLoadMoreButton() {
    const button = this.refs.loadMoreButton;
    if (!button || Array.isArray(button)) return;

    if (this.hasNextPage) {
      button.removeAttribute('hidden');
    } else {
      button.setAttribute('hidden', '');
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
